import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Quote, Star, X, MapPin } from "lucide-react";
import { getTestimonials } from "../../../api/testimonialApi";
import { TestimonialShimmer } from "../../../components/ShimmerEffect";

const CHAR_LIMIT = 120;

/* ── Premium Review Modal ─────────────────────────────────────────────────── */
const ReviewModal = ({ testimonial, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const t = testimonial;

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />

      {/* Modal card */}
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        style={{
          background: "rgba(255,255,255,0.97)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient accent bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-800 transition-all hover:scale-110 z-10"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="p-8 dark:bg-gray-900">
          {/* Big quote icon */}
          <div className="mb-5 -mt-1">
            <Quote
              size={52}
              className="text-blue-500 opacity-85 rotate-180"
              fill="currentColor"
            />
          </div>

          {/* Full review text */}
          <p className="text-gray-800 dark:text-gray-100 text-[15.5px] leading-[1.75] font-normal">
            {t.comment}
          </p>

          {/* Stars */}
          <div className="flex items-center gap-1 mt-5">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={15}
                className={
                  i < (t.rating || 5)
                    ? "fill-yellow-400 text-yellow-400"
                    : "fill-gray-200 text-gray-200"
                }
              />
            ))}
            <span className="ml-1 text-xs text-gray-400 font-semibold">
              {t.rating || 5}/5
            </span>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 dark:border-gray-700 mt-5 pt-5 flex items-center gap-3">
            {t.image ? (
              <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 border-2 border-blue-100">
                <img
                  src={t.image}
                  alt={t.customer}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-black text-xl shrink-0 shadow-md">
                {t.customer ? t.customer.charAt(0).toUpperCase() : "C"}
              </div>
            )}
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                {t.customer}
              </p>
              {t.location && (
                <p className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5">
                  <MapPin size={10} />
                  {t.location}
                </p>
              )}
              <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                Verified Buyer
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Testimonial Card ─────────────────────────────────────────────────────── */
const TestimonialCard = ({ t, onReadMore }) => {
  const isLong = t.comment && t.comment.length > CHAR_LIMIT;
  const preview = isLong ? t.comment.slice(0, CHAR_LIMIT).trimEnd() + "…" : t.comment;

  return (
    <div className="w-[280px] sm:w-[295px] lg:w-[280px] xl:w-[290px] shrink-0 snap-center flex flex-col bg-white dark:bg-[#111] rounded-2xl border border-gray-100 dark:border-gray-800 shadow-[0_4px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_32px_rgba(0,0,0,0.4)] hover:-translate-y-2 transition-transform duration-300 overflow-hidden">
      {/* Top accent */}
      <div className="h-1 w-full bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="p-6 sm:p-7 flex flex-col flex-1">
        {/* Quote icon */}
        <div className="mb-3 -mt-1">
          <Quote
            size={42}
            className="text-blue-600 dark:text-blue-500 rotate-180 opacity-80"
            fill="currentColor"
          />
        </div>

        {/* Comment — full if short, preview + button if long */}
        <div className="flex-grow">
          <p className="text-gray-800 dark:text-gray-200 text-[14.5px] leading-relaxed">
            {preview}
          </p>
          {isLong && (
            <button
              onClick={() => onReadMore(t)}
              className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors group/btn"
            >
              Read full review
              <span className="inline-block transition-transform duration-200 group-hover/btn:translate-x-0.5">›</span>
            </button>
          )}
        </div>

        {/* Stars */}
        <div className="flex items-center gap-0.5 mt-4">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={12}
              className={
                i < (t.rating || 5)
                  ? "fill-yellow-400 text-yellow-400"
                  : "fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700"
              }
            />
          ))}
        </div>

        {/* Customer Info */}
        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
          {t.image ? (
            <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-gray-200 dark:border-gray-700">
              <img src={t.image} alt={t.customer} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-black text-base shrink-0 shadow-sm">
              {t.customer ? t.customer.charAt(0).toUpperCase() : "C"}
            </div>
          )}
          <div>
            <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
              {t.customer}
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-1">
              {t.location && <MapPin size={9} />}
              {t.location || "Verified Buyer"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main Section ─────────────────────────────────────────────────────────── */
const TestimonialSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [testimonials]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await getTestimonials();
        if (response.success && response.data && response.data.length > 0) {
          setTestimonials(response.data);
        } else {
          setTestimonials([
            {
              _id: "dummy1",
              customer: "Ratikant Gokhale",
              location: "Gurgaon",
              rating: 5,
              comment:
                "I loved that Bright Laptop delivered my laptop instantly. It was super convenient since I'm always busy.",
            },
            {
              _id: "dummy2",
              customer: "Harpreet Singh",
              location: "Haldwani",
              rating: 5,
              comment:
                "I tried buying a laptop locally, but didn't have any luck. Bright Laptop helped me buy a certified laptop in just minutes without any stress.",
            },
            {
              _id: "dummy3",
              customer: "Bagesh Kumar",
              location: "Agra",
              rating: 5,
              comment:
                "The laptop was in extremely good condition, and I was surprised that Bright Laptop offered such a great deal. Really happy with the whole experience.",
            },
            {
              _id: "dummy4",
              customer: "Shubham Ghunawat",
              location: "Delhi",
              rating: 5,
              comment:
                "I was nervous about buying a refurbished laptop online, but Bright Laptop made it simple with no hassles and great support. Their team was responsive and the delivery was on time. Highly recommend!",
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const scrollContainer = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo =
        direction === "left" ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };

  if (loading) return <TestimonialShimmer />;

  return (
    <>
      <section className="pt-8 pb-0 bg-white dark:bg-black overflow-hidden relative">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-extrabold text-black dark:text-white sm:text-4xl tracking-tight">
              What Customers Think About Us
            </h2>
            <p className="mt-2 text-sm sm:text-base text-gray-500 dark:text-gray-400">
              We deeply care about every single review.
            </p>
          </div>

          <div className="relative group">
            {/* Left Scroll Button */}
            {canScrollLeft && (
              <button
                onClick={() => scrollContainer("left")}
                className="absolute left-[-16px] sm:left-[-20px] top-[50%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700 transition-all hover:scale-105"
                aria-label="Scroll left"
              >
                <ChevronLeft size={24} />
              </button>
            )}

            {/* Scroll Container */}
            <div
              ref={scrollRef}
              onScroll={checkScroll}
              className="flex overflow-x-auto pb-6 pt-2 gap-5 snap-x scrollbar-hide scroll-smooth"
            >
              {testimonials.map((t) => (
                <TestimonialCard
                  key={t._id}
                  t={t}
                  onReadMore={setActiveModal}
                />
              ))}
            </div>

            {/* Right Scroll Button */}
            {canScrollRight && testimonials.length > 0 && (
              <button
                onClick={() => scrollContainer("right")}
                className="absolute right-[-16px] sm:right-[-20px] top-[50%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700 transition-all hover:scale-105"
                aria-label="Scroll right"
              >
                <ChevronRight size={24} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Review Modal */}
      {activeModal && (
        <ReviewModal
          testimonial={activeModal}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
};

export default TestimonialSection;
